import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ExternalLink } from "lucide-react";

function fmtDateTime(s) {
    try {
        return new Date(s).toLocaleString();
    } catch {
        return s;
    }
}

export default function MockEmailsPage() {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/mock-emails");
                setEmails(data);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <DashboardShell>
            <div className="fade-up">
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    Operations · Mock emails
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    Mock email outbox
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-600 leading-relaxed">
                    Phase 2 logs invite emails here instead of sending them. Use this to verify links and message content end-to-end.
                </p>
            </div>

            <div className="mt-8 space-y-4">
                {loading && <p className="text-sm text-slate-500" data-testid="mock-emails-loading">Loading…</p>}
                {!loading && emails.length === 0 && (
                    <Card data-testid="mock-emails-empty" className="border-dashed border-2 border-slate-200 bg-slate-50/60">
                        <CardContent className="py-14 text-center">
                            <Mail className="mx-auto mb-3 text-slate-300" size={28} />
                            <p className="font-display text-base font-semibold text-slate-900">No mock emails yet</p>
                            <p className="mt-1 text-sm text-slate-500">Create an invite — its mock email will appear here.</p>
                        </CardContent>
                    </Card>
                )}
                {!loading &&
                    emails.map((m) => (
                        <Card key={m.id} data-testid={`mock-email-${m.id}`}>
                            <CardContent className="p-5">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-wider text-slate-500">{fmtDateTime(m.created_at)}</div>
                                        <h3 className="font-display text-lg font-semibold text-slate-900 mt-1">{m.subject}</h3>
                                        <p className="text-sm text-slate-500 mt-0.5">
                                            To: <span className="font-mono text-xs">{m.to}</span>
                                        </p>
                                    </div>
                                    {m.invite_url && (
                                        <a
                                            href={m.invite_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`open-mock-link-${m.id}`}
                                            className="inline-flex items-center gap-1 text-sm font-medium text-blue-900 hover:underline shrink-0"
                                        >
                                            Open link <ExternalLink size={12} />
                                        </a>
                                    )}
                                </div>
                                <pre className="mt-4 whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-4 font-sans leading-relaxed">
                                    {m.body}
                                </pre>
                            </CardContent>
                        </Card>
                    ))}
            </div>
        </DashboardShell>
    );
}
